<template>
  <div class="fee-settings-particulars">
    <v-row>
      <v-col cols="12" md="8" v-if="structure">
        <h1 class="text-left mx-4 mt-2">{{ structure.room }}</h1>
        <h5 class="text-left mx-4">{{activeFeeSession.title }}</h5>
        
        <h2 class="text--secondary text-left mx-4">{{ structure.category }}</h2>
        <p class="text--secondary text-left mx-4 my-2">Select a schedule, create or delete instalments as per convenience.</p>
      </v-col>
    </v-row>
    <v-card class="mx-4 my-4">
      <v-data-table
        :headers="headers"
        :items="particulars"
        show-expand
        single-expand
        @click:row="clickedRow"
        :expanded="expanded"
        class="row-pointer"
      >
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <h3 v-if="item.instalments.length" class="mt-2 text-left">Instalments</h3>
            <v-row v-else justify="center" align="center">
              <v-col cols="12" md="6" v-if="schedules">
                <h3 class="mt-2 mb-2">Select a Fee Schedule</h3>
                <!-- if a schedule is already selected, show that schedule only, do not allow selection of other elements -->
                <v-select
                  v-model="selectedSchedule"
                  :items="savedSchedule ? [savedSchedule] : schedules"
                  :item-text="item=>`${item.title} (${item.instalments.length} Instalments)`"
                  return-object
                  filled
                  hide-details
                >
                  <!-- <template v-slot:selection="{ item }">
                      {{item.title}} ({{item.instalments.length}} Instalments)
                  </template>-->
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{item.title}}</v-list-item-title>
                      <v-list-item-subtitle>{{item.instalments.length}} Instalments</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-select>
                <v-btn @click="saveSelectedSchedule(item)" class="mt-3" color="primary">Save</v-btn>
              </v-col>
            </v-row>
            <v-row v-for="instalment in item.instalments" :key="instalment.id">
              <!-- Name -->
              <v-col cols="12" md="3">
                <p class="text-left mx-2 mb-1">Instalment Name</p>
                <p class="text-left mx-2">
                  <b>{{instalment.schedule.title}}</b>
                </p>
              </v-col>
              <!-- Start Date -->
              <v-col cols="12" md="3">
                <p class="text-left mx-2 mb-1">Start Date</p>
                <p class="text-left mx-2">
                  <b>{{moment(instalment.schedule.start_date).format('D MMM, YYYY')}}</b>
                </p>
              </v-col>
              <!-- End Date -->
              <v-col cols="12" md="3">
                <p class="text-left mx-2 mb-1">Last Payment Date</p>
                <p class="text-left mx-2">
                  <b>{{moment(instalment.schedule.end_date).format('D MMM, YYYY')}}</b>
                </p>
              </v-col>
              <!-- Amount -->
              <v-col cols="11" md="2">
                <p class="text-left mx-2 mb-1">Amount</p>
                <p v-if="!isInstalmentsEditing && instalment.id" class="text-left mx-2">
                  <b>{{ instalment.amount }}</b>
                </p>
                <v-tooltip :disabled="!instalment.is_transaction_done" top v-else>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-text-field
                        v-model="instalment.amount"
                        outlined
                        type="number"
                        dense
                        :disabled="instalment.is_transaction_done"
                        :rules="[rules.no_negative, rules.required]"
                        class="rounded-input"
                        min="0"
                      ></v-text-field>
                    </div>
                  </template>
                  <span>Fee Collection for this instalment already done. Cannot edit now.</span>
                </v-tooltip>
              </v-col>
              <!-- Delete Icon -->
              <v-col cols="1" md="1">
                <p class="text-left mt-4">
                  <v-btn @click="showDeleteDialog(instalment, item.instalments)" icon>
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </p>
              </v-col>
              <v-divider
                v-if="item.instalments.indexOf(instalment) != item.instalments.length -1"
                class="mx-4"
              />
            </v-row>
            <v-btn
              class="mb-3"
              v-if="item.instalments.length && item.instalments[0].id && !isInstalmentsEditing"
              color="primary"
              @click="isInstalmentsEditing=true"
              :disabled="isLoading"
            >Edit Instalments</v-btn>
            <v-btn
              class="mb-3"
              v-else-if="item.instalments.length"
              color="primary"
              :disabled="isLoading || totalSum(item.instalments, 'amount') <= 0"
              @click="saveParticularInstalments(item)"
            >Save Instalments</v-btn>
          </td>
        </template>
        <template v-slot:item.title="{item}">
          <v-avatar color="primary" size="36" rounded>
            <span class="white--text px-1 font-weight-bold">{{ item.code }}</span>
          </v-avatar>
          <span class="ml-2">
            <b>{{ item.title }}</b>
          </span>
        </template>
        <!-- <template v-slot:item.status="{item}">
            <v-switch v-model="item.status"></v-switch>
        </template>-->
        <template v-slot:item.instalments="{item}">
          <b>{{ item.instalments.length }}</b>
        </template>
        <template v-slot:item.total_fees="{item}">
          <b>{{ `${currency_format(totalInstalmentFees(item.instalments))}` }}</b>
        </template>
      </v-data-table>
    </v-card>
    <v-snackbar v-model="snackbar.visible">
      {{ snackbar.text }}
      <template>
        <v-btn color="pink" text @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
    <DeleteConfirmationDialog
      title="Are you sure you want to delete this Fee Instalment?"
      description="You will not be able to undo this action."
      :visible="showInstalmentDeleteConfirmation"
      @deleteObject="(confirmation) => deleteInstalment(confirmation)"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import endpoints from "@api/repository";
import api from "@api/index";
import moment from "moment";
import Helper from "@utils/misc";
import DeleteConfirmationDialog from "@components/widgets/dialogs/DeleteConfirmationDialog";
export default {
  name: "FeeSettingsStructureCategoryDetails",
  components: {
    DeleteConfirmationDialog,
  },
  data() {
    return {
      showInstalmentDeleteConfirmation: false,
      toBeDeletedInstalment: null,
      toBeDeletedFromInstalmentList: null,
      moment: moment,
      isInstalmentsEditing: false,
      selectedSchedule: null,
      savedSchedule: null,
      expanded: [],
      rules: {
        no_negative: (value) => value >= 0 || "Invalid",
        required: (value) => !!value || "Required",
      },
      snackbar: {
        visible: false,
        message: "",
      },
      headers: [
        { text: "Particular", value: "title" },
        // { text: "Status", value: "status" },
        { text: "Instalments", value: "instalments" },
        { text: "Amount", value: "total_fees" },
        { text: "", align: "center", value: "data-table-expand" },
      ],
      schedules: [],
      particulars: [],
      structure: null,
    };
  },
  computed: {
    ...mapGetters(["currentUser", "accessToken", "isLoading","activeFeeSession"]),
    essentials() {
      return {
        accessToken: this.accessToken,
        setLoading: this.setLoading,
        handleErrorsFunction: this.handleApiErrors,
      };
    },
  },
  methods: {
    ...mapActions(["setLoading"]),
    totalSum: Helper.totalSum,
    totalInstalmentFees(instalments) {
      var total = 0.0;
      instalments.map((i) => (total += Number(i.amount)));
      return total;
    },

    showDeleteDialog(instalment, instalmentList) {
      if (instalment.is_transaction_done) {
        this.snackbar.text =
          "Cannot be deleted since Fee Collection for students have already been done using this data";
        this.snackbar.visible = true;
        return;
      }
      this.showInstalmentDeleteConfirmation = true;
      this.toBeDeletedInstalment = instalment;
      this.toBeDeletedFromInstalmentList = instalmentList;
    },

    // gets basic details of structure: category and room
    async getBasicStructureDetails() {
      var response;
      var url =  endpoints.getBasicStructureDetails +
          this.$route.params.rid +
          "/" +
          this.$route.params.cid +
          "/" + `?session=${this.activeFeeSession.id}`;
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url : url,
        responseVar: response,
      });
      if (response) {
        this.structure = response;
      }
    },

    // gets all particulars based on the room
    async getRoomParticulars() {
      var response;
      var url = endpoints.getAllRoomParticulars +
          this.$route.params.rid +
          "/" +
          this.$route.params.cid +
          "/" + `?session=${this.activeFeeSession.id}`;
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: url,
        responseVar: response,
      });
      if (response) {
        this.particulars = response;
      }
    },

    // gets all particulars based on the room
    async getRoomSchedules() {
      var response;
      var url =  endpoints.getAllRoomSchedules + this.$route.params.rid + `/?session=${this.activeFeeSession.id}` ;
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: url,
        responseVar: response,
      });
      if (response) {
        this.schedules = response;
      }
    },

    // saves a selected schedule and shows instalments associated with schedule
    async saveSelectedSchedule(particular) {
      // create feeinstalments based on number of fee schedule instalments of schedule

      this.selectedSchedule.instalments.map((schedule) => {
        particular.instalments.push({
          schedule: schedule,
          amount: 0.0,
          fee_category: this.$route.params.cid,
          particular: particular.id,
        });
      });
      // clear selectedSchedule
      this.savedSchedule = this.selectedSchedule;
      this.selectedSchedule = null;
    },

    // sends particular instalments to server and retrives saved objects
    async saveParticularInstalments(item) {
      var response;
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: endpoints.saveParticularInstalment,
        responseVar: response,
        method: api.Methods.POST,
        body: {
          instalments: item.instalments,
          room_id: this.$route.params.rid,
          session: this.activeFeeSession.id,
        },
      });
      if (response) {
        this.isInstalmentsEditing = false;
        item.instalments = response;
      }
    },

    async deleteInstalment(confirmation) {
      this.showInstalmentDeleteConfirmation = false;
      var instalment = this.toBeDeletedInstalment;
      var instalmentsList = this.toBeDeletedFromInstalmentList;
      this.toBeDeletedInstalment = null;
      this.toBeDeletedFromInstalmentList = null;
      if (!confirmation) return;
      var self = this;
      var removeInstalment = (instalment, instalmentsList) => {
        var index = instalmentsList.indexOf(instalment);
        instalmentsList.splice(index, 1);
        self.resetSavedSchedule(); //reset saved schedule if no instalments left
      };
      if (!instalment.id) {
        removeInstalment(instalment, instalmentsList);
        return;
      }
      var response;
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: endpoints.deleteParticularInstalment,
        responseVar: response,
        method: api.Methods.POST,
        body: {
          instalment_id: instalment.id,
        },
      });
      if (response == true) {
        removeInstalment(instalment, instalmentsList);
      }
    },

    // resets saved schedule only if all instalments are empty
    async resetSavedSchedule() {
      if (this.particulars.find((p) => p.instalments.length > 0) != null)
        return; //if any one particular is found with instalments more than 0, end this function
      this.savedSchedule = null;
    },

    clickedRow(value) {
      const index = this.expanded.indexOf(value);
      if (index === -1) {
        this.expanded.push(value);
      } else {
        this.expanded.splice(index, 1);
      }
    },

    currency_format(amount) {
      var formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "INR",
      });
      return formatter.format(amount);
    },
  },
  async handleApiErrors(err) {
    this.setLoading(false);
    console.log(err);
    if (err.response) {
      console.log(err.response);
    } else {
      this.snackbar.text = "Unable to process request";
      this.snackbar.visible = true;
    }
  },
  created() {
    this.getBasicStructureDetails();
    this.getRoomParticulars();
    this.getRoomSchedules();
  },
};
</script>

<style scoped>
h2 {
  font-size: 24px;
}
.row-pointer {
  cursor: pointer;
}
</style>